import { useCallback, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';

/**
 * A confetti burst that belongs to the component that fired it.
 *
 * The default `confetti()` export draws on ONE global canvas appended to
 * document.body and shared by every caller, which caused two bugs:
 *  - the burst outlived its route, so /try's celebration kept animating over
 *    /dashboard, /quiz/loading and the first quiz question;
 *  - calling the global `confetti.reset()` to stop it also cancelled bursts
 *    fired by other components (CelebrationOverlay lives at the app root and
 *    celebrates the very `diagnostic_done` milestone /try completion triggers).
 *
 * `confetti.create` gives each caller its own canvas, so unmounting resets only
 * that canvas and the global one is never touched.
 *
 * Render the canvas yourself so it unmounts with the page:
 *   <canvas ref={canvasRef} className={OWNED_CONFETTI_CANVAS_CLASS} aria-hidden="true" />
 */
export const OWNED_CONFETTI_CANVAS_CLASS = 'pointer-events-none fixed inset-0 z-[60]';

interface OwnedConfettiOptions {
  /**
   * Off by default, and that is the isolation-preserving choice: canvas-confetti's
   * worker is a MODULE-LEVEL singleton, and worker.init(canvas) retargets it to
   * whichever canvas most recently called transferControlToOffscreen(). Two
   * "owned" instances would therefore share one worker and stomp on each other,
   * which is the exact problem this hook exists to prevent. It is also the wrong
   * trade for a streamer loop: two calls per animation frame for three particles
   * each becomes a postMessage plus a structured clone per call.
   */
  useWorker?: boolean;
}

export const useOwnedConfetti = ({ useWorker = false }: OwnedConfettiOptions = {}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const instanceRef = useRef<confetti.CreateTypes | null>(null);

  const fire = useCallback(
    (options: confetti.Options) => {
      if (!canvasRef.current) return;
      if (!instanceRef.current) {
        // resize keeps the canvas matched to the viewport. The instance is
        // per-canvas and resets independently with or without the worker.
        instanceRef.current = confetti.create(canvasRef.current, { resize: true, useWorker });
      }
      instanceRef.current(options);
    },
    [useWorker]
  );

  useEffect(
    () => () => {
      instanceRef.current?.reset();
      instanceRef.current = null;
    },
    []
  );

  return { canvasRef, fire };
};
